import { Link } from "react-router-dom";
import { Star, ArrowRight, MapPin } from "lucide-react";
import DOMPurify from "dompurify";
import { useSEO } from "@/hooks/useSEO";

// Placeholder review set. [REPLACE ME] with real testimonials.
const REVIEWS = [
  { name: "Denise R.", city: "Fort Myers, FL", tag: "Inherited property",
    quote: "Inherited my mother's house in Cape Coral. Full of stuff, roof leaking, way too much for me to deal with from out of state. FlipPilot came through with a fair cash offer in 18 hours and closed 9 days later. I didn't lift a finger.",
    stars: 5 },
  { name: "Marcus T.", city: "Lehigh Acres, FL", tag: "Tired landlord",
    quote: "Landlord life burned me out. My last tenant left the place trashed. I couldn't afford another 3 months of vacancy. Sold to FlipPilot as-is, walked away with a check, done. Best decision I made all year.",
    stars: 5 },
  { name: "Karen S.", city: "Naples, FL", tag: "Pre-foreclosure",
    quote: "I was 45 days from foreclosure and terrified. FlipPilot was the only buyer who actually explained the whole process without pressure. Closed in 11 days and I kept enough to move my family into an apartment with breathing room.",
    stars: 5 },
  { name: "Jorge P.", city: "Port Charlotte, FL", tag: "Divorce sale",
    quote: "Divorce forced a sale. My ex and I couldn't agree on a realtor, and every retail buyer wanted repairs we didn't have time or money for. FlipPilot's offer was $12K higher than the local iBuyer and they didn't renegotiate at close. Straight up.",
    stars: 5 },
  { name: "Aisha L.", city: "Bonita Springs, FL", tag: "Job relocation",
    quote: "Job relocated me to Texas with two weeks' notice. Listed with an agent first. 3 months, no offers, still paying the mortgage. Switched to FlipPilot on a Tuesday. Signed by Thursday, closed the next Friday. Wish I'd called them first.",
    stars: 5 },
  { name: "Robert &amp; Lisa M.", city: "Cape Coral, FL", tag: "Downsizing",
    quote: "Downsizing after 32 years in the same house. We didn't have the energy for showings and open houses. FlipPilot gave us a full weekend to think about the offer, no pressure. Signed Monday, closed 3 weeks later, kept every stick of furniture we wanted.",
    stars: 5 },
];

export default function Reviews() {
  useSEO({
    title: "Reviews. What Sellers Say About FlipPilot",
    description:
      "Read real reviews from Southwest Florida homeowners who sold their houses fast to FlipPilot. 5.0 average rating across inherited properties, divorce sales, pre-foreclosures, and relocations.",
    path: "/reviews",
    keywords: "FlipPilot reviews, cash home buyer testimonials Florida, sell house fast reviews Cape Coral",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: "FlipPilot",
      url: "https://flippilot.us",
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "5.0",
        reviewCount: String(REVIEWS.length),
      },
      review: REVIEWS.map((r) => ({
        "@type": "Review",
        author: { "@type": "Person", name: r.name },
        reviewRating: { "@type": "Rating", ratingValue: r.stars, bestRating: 5 },
        reviewBody: r.quote,
      })),
    },
  });

  return (
    <>
      <section className="pt-16 pb-12" data-testid="reviews-hero">
        <div className="mkt-container">
          <span className="mkt-chip"><MapPin size={12} />Southwest Florida</span>
          <h1 className="mkt-hero-title mt-6 max-w-3xl">
            5.0 <span style={{ color: "var(--mkt-accent)" }}>★★★★★</span> from real sellers.
          </h1>
          <p className="mt-5 text-lg text-[var(--mkt-text-muted)] max-w-2xl">
            We only ask sellers to leave a review after their money hits the
            bank. Here&rsquo;s what they&rsquo;ve said.
          </p>
        </div>
      </section>

      <section className="pb-24" data-testid="reviews-grid">
        <div className="mkt-container grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {REVIEWS.map((r, i) => (
            <article key={i} className="mkt-card p-6" data-testid={`review-${i}`}>
              <div className="flex items-center gap-1 mb-3">
                {[...Array(r.stars)].map((_, j) => (
                  <Star key={j} size={14} fill="var(--mkt-accent)" stroke="var(--mkt-accent)" />
                ))}
              </div>
              <p className="text-sm leading-relaxed">&ldquo;{r.quote}&rdquo;</p>
              <div className="mt-5 pt-4 border-t border-[var(--mkt-line)] flex items-center justify-between">
                <div>
                  <div className="font-semibold text-sm" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(r.name) }} />
                  <div className="text-xs text-[var(--mkt-text-muted)]">{r.city}</div>
                </div>
                <span className="mkt-chip">{r.tag}</span>
              </div>
            </article>
          ))}
        </div>
        <div className="text-center mt-14">
          <Link to="/contact" className="mkt-btn mkt-btn-primary">
            Be Our Next Review
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </>
  );
}
